import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { usePost } from '../API';
import { type User } from '@aidkitorg/aidkit/lib/users/UserService';
import { type UserGroup } from '@aidkitorg/aidkit/lib/users/UserGroupService';
import { createUser, updateUser, createUserGroup, updateUserGroup } from '@aidkitorg/aidkit/lib/users/user_management';
import { Organization } from './OrganizationContext';

type CreateUserParams = Parameters<typeof createUser>[0];
type UpdateUserParams = Omit<Parameters<typeof updateUser>[0], 'uid'>;
type CreateUserGroupParams = Parameters<typeof createUserGroup>[0];
type UpdateUserGroupParams = Omit<Parameters<typeof updateUserGroup>[0], 'id'>;

export type UserContextType = {
  users: User[];
  userGroups: UserGroup[];
  editableTags: string[];
  applicantGroups: any[];
  applicantGroupsRequireSelection: boolean;
  isAdmin: boolean;
  isUsersLoading: boolean;
  isUserGroupsLoading: boolean;

  loadUsers: (filters?: { organization?: string | null; filter?: string; user_group?: number }) => void;
  loadUserGroups: (organization?: string | null) => void;
  loadEditableTags: () => void;
  loadApplicantGroups: () => void;
  getUserGroupById: (id: number) => Promise<UserGroup | null>;
  createUser: (userData: CreateUserParams) => Promise<boolean>;
  updateUser: (uid: string, updates: UpdateUserParams) => Promise<boolean>;
  deleteUser: (uid: string) => Promise<boolean>;
  resendWelcomeEmail: (uid: string) => Promise<boolean>;
  createUserGroup: (groupData: CreateUserGroupParams) => Promise<{ success: boolean; group?: any }>;
  updateUserGroup: (id: number, updates: UpdateUserGroupParams) => Promise<boolean>;
  deleteUserGroup: (id: number) => Promise<boolean>;
};

const UserContext = createContext<UserContextType>({
  users: [],
  userGroups: [],
  editableTags: [],
  applicantGroups: [],
  applicantGroupsRequireSelection: false,
  isAdmin: false,
  isUsersLoading: false,
  isUserGroupsLoading: false,

  loadUsers: () => {},
  loadUserGroups: () => {},
  loadEditableTags: () => {},
  loadApplicantGroups: () => {},
  getUserGroupById: async () => null,
  createUser: async () => false,
  updateUser: async () => false,
  deleteUser: async () => false,
  resendWelcomeEmail: async () => false,
  createUserGroup: async () => ({ success: false }),
  updateUserGroup: async () => false,
  deleteUserGroup: async () => false,
});

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

type UserProviderProps = {
  children: ReactNode;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [editableTags, setEditableTags] = useState<string[]>([]);
  const [applicantGroups, setApplicantGroups] = useState<any[]>([]);
  const [applicantGroupsRequireSelection, setApplicantGroupsRequireSelection] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isUserGroupsLoading, setIsUserGroupsLoading] = useState(false);

  // API hooks
  const listUsersApi = usePost('/user/list');
  const createUserApi = usePost('/user/create');
  const updateUserApi = usePost('/user/update');
  const deleteUserApi = usePost('/user/delete');
  const resendWelcomeEmailApi = usePost('/user/resend_welcome_email');
  const listUserGroupsApi = usePost('/user-group/list');
  const getUserGroupByIdApi = usePost('/user-group/get');
  const createUserGroupApi = usePost('/user-group/create');
  const updateUserGroupApi = usePost('/user-group/update');
  const deleteUserGroupApi = usePost('/user-group/delete');
  const getEditableTagsApi = usePost('/tags/list');
  const getApplicantGroupsApi = usePost('/applicant-groups/list');

  // Computed values
  const isAdmin = editableTags.includes('admin');

  // User operations
  const loadUsers = useCallback(async (filters?: { organization?: string | null; filter?: string; user_group?: number }) => {
    setIsUsersLoading(true);
    const organization = filters?.organization;
    const response = await listUsersApi({
      organization,
      filter: filters?.filter,
      user_group: filters?.user_group
    });

    if (Array.isArray(response)) {
      setUsers(response);
    }
    setIsUsersLoading(false);
  }, [listUsersApi]);

  const createUser = useCallback(async (userData: CreateUserParams): Promise<boolean> => {
    const response = await createUserApi(userData);
    if (response && typeof response === 'object') {
      return true;
    }
    return false;
  }, [createUserApi]);

  const updateUser = useCallback(async (uid: string, updates: UpdateUserParams): Promise<boolean> => {
    const response = await updateUserApi({ uid, ...updates });
    if (response && typeof response === 'object') {
      // Don't automatically update users array - let the caller reload users to get properly formatted data
      return true;
    }
    return false;
  }, [updateUserApi]);

  const deleteUser = useCallback(async (uid: string): Promise<boolean> => {
    await deleteUserApi({ uid });
    // Don't automatically remove from users array - let the caller reload users to get properly formatted data
    return true;
  }, [deleteUserApi]);

  const resendWelcomeEmail = useCallback(async (uid: string): Promise<boolean> => {
    const response = await resendWelcomeEmailApi({ uid });
    return !!response?.success;
  }, [resendWelcomeEmailApi]);

  // User Group operations
  const loadUserGroups = useCallback(async (organization?: string | null) => {
    setIsUserGroupsLoading(true);
    const response = await listUserGroupsApi({
      organization
    });

    if (Array.isArray(response)) {
      setUserGroups(response);
    }
    setIsUserGroupsLoading(false);
  }, [listUserGroupsApi]);

  const getUserGroupById = useCallback(async (id: number): Promise<UserGroup | null> => {
    const response = await getUserGroupByIdApi({ id });
    if (response && typeof response === 'object') {
      return response as UserGroup;
    }
    return null;
  }, [getUserGroupByIdApi]);

  const createUserGroup = useCallback(async (groupData: CreateUserGroupParams): Promise<{ success: boolean; group?: any }> => {
    const response = await createUserGroupApi(groupData);
    if (response && typeof response === 'object') {
      // Let the caller handle refetching to get properly formatted data with user counts
      return { success: true, group: response };
    }
    return { success: false };
  }, [createUserGroupApi]);

  const updateUserGroup = useCallback(async (id: number, updates: UpdateUserGroupParams): Promise<boolean> => {
    const response = await updateUserGroupApi({ id, ...updates });
    if (response && typeof response === 'object') {
      // Let the caller handle refetching to get properly formatted data with user counts
      return true;
    }
    return false;
  }, [updateUserGroupApi]);

  const deleteUserGroup = useCallback(async (id: number): Promise<boolean> => {
    await deleteUserGroupApi({ id });
    // Let the caller handle refetching to get properly formatted data with user counts
    return true;
  }, [deleteUserGroupApi]);

  // Tags operations
  const loadEditableTags = useCallback(async () => {
    const response = await getEditableTagsApi({});

    if (Array.isArray(response)) {
      setEditableTags(response);
    }
  }, [getEditableTagsApi]);

  const loadApplicantGroups = useCallback(async () => {
    const response = await getApplicantGroupsApi({});

    if (response && typeof response === 'object' && 'applicantGroups' in response) {
      // New format with requiresSelection flag
      setApplicantGroups(response.applicantGroups || []);
      setApplicantGroupsRequireSelection(response.requiresSelection || false);
    }
  }, [getApplicantGroupsApi]);

  const contextValue: UserContextType = {
    users,
    userGroups,
    editableTags,
    applicantGroups,
    applicantGroupsRequireSelection,
    isAdmin,
    isUsersLoading,
    isUserGroupsLoading,

    loadUsers,
    loadUserGroups,
    loadEditableTags,
    loadApplicantGroups,
    getUserGroupById,
    createUser,
    updateUser,
    deleteUser,
    resendWelcomeEmail,
    createUserGroup,
    updateUserGroup,
    deleteUserGroup,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};